import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { UseFormReturn } from 'react-hook-form';
import type { RoleFormValues } from '../hooks/useRoleForm';
import type { Permission } from '../types';

interface PermissionsGridProps {
  form: UseFormReturn<RoleFormValues>;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedPermissions: string[];
  isLoadingPermissions: boolean;
  groupedPermissions: Record<string, Permission[]>;
  togglePermission: (permName: string) => void;
  selectAll: () => void;
  deselectAll: () => void;
  selectFiltered: () => void;
  deselectFiltered: () => void;
}

export function PermissionsGrid({
  form,
  searchQuery,
  setSearchQuery,
  selectedPermissions,
  isLoadingPermissions,
  groupedPermissions,
  togglePermission,
  selectAll,
  deselectAll,
  selectFiltered,
  deselectFiltered,
}: PermissionsGridProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4 pt-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          {t('permissions')}
          <span className="ml-2 text-xs font-normal normal-case">
            ({selectedPermissions.length} {t('selected')})
          </span>
        </h3>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={searchQuery ? selectFiltered : selectAll}
          >
            {t('selectAll')}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={searchQuery ? deselectFiltered : deselectAll}
          >
            {t('deselectAll')}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t('searchPermissions')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Permissions Grid */}
      <FormField
        control={form.control}
        name="permissions"
        render={() => (
          <FormItem>
            <FormControl>
              <div className="border rounded-lg max-h-[320px] overflow-y-auto">
                {isLoadingPermissions ? (
                  <div className="p-4 space-y-3">
                    {['group-1', 'group-2', 'group-3'].map((groupId) => (
                      <div key={groupId} className="space-y-2">
                        <Skeleton className="h-4 w-24" />
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                          {['item-1', 'item-2', 'item-3', 'item-4'].map((itemId) => (
                            <Skeleton key={itemId} className="h-8" />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : Object.keys(groupedPermissions).length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    {searchQuery ? t('noPermissionsFound') : t('noPermissionsAvailable')}
                  </div>
                ) : (
                  <div className="divide-y">
                    {Object.entries(groupedPermissions).map(([resource, perms]) => (
                      <div key={resource} className="p-3">
                        <h4 className="text-sm font-medium text-foreground mb-2 capitalize">
                          {resource.replace(/-/g, ' ')}
                        </h4>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5">
                          {perms.map((perm) => {
                            const isSelected = selectedPermissions.includes(perm.name);
                            const action = perm.name.split(':')[1] || perm.name;

                            return (
                              <label
                                key={perm.id}
                                className={cn(
                                  'flex items-center gap-2 px-2.5 py-1.5 rounded-md cursor-pointer transition-colors text-sm',
                                  isSelected
                                    ? 'bg-primary/10 text-primary border border-primary/20'
                                    : 'hover:bg-muted border border-transparent'
                                )}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => togglePermission(perm.name)}
                                  className="h-4 w-4"
                                />
                                <span className="truncate capitalize">
                                  {action.replace(/-/g, ' ')}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
